import React, { FC, ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../assets/icons/index';
import { Tooltip } from '../../../components/tooltip/index';
import { useRevealClip } from '../../../hooks/use-reveal-clip.hook';

type DropDownProps = {
  children: ReactNode;
  className?: string;
  isOpenOnStart?: boolean;
  title: string;
};

/**
 * Per-stakeholder document section in the SAFE conversion modal. Mirrors the event form's
 * collapsible (`events/event-form/components/form-drop-down.tsx`) down to the timing, so the two
 * flows expand the same way.
 */
export const FormDropDown: FC<DropDownProps> = ({
  title,
  children,
  className,
  isOpenOnStart = true,
}) => {
  const [isOpen, setOpen] = useState(isOpenOnStart);
  const isClipped = useRevealClip(isOpen);

  return (
    <div className={twMerge('flex w-full flex-col', className)}>
      <div
        className={twMerge(
          'flex w-full items-center justify-between rounded-md border-[1px] px-4 py-[10px] transition-[background-color,border-color,border-radius] duration-200 ease-in-out',
          isOpen ? 'rounded-b-none border-transparent bg-gray-50' : 'border-gray-100 bg-gray-25',
        )}
      >
        <Tooltip childrenWrapperClassName="max-w-[320px]" content={title}>
          <span className="truncate text-sm font-[450] text-gray-700">{title}</span>
        </Tooltip>
        <div className="flex items-center gap-4">
          <div
            className="h-fit w-fit cursor-pointer p-1 transition-colors hover:bg-gray-50"
            onClick={() => {
              setOpen((prev) => !prev);
            }}
          >
            <ChevronDownIcon
              className={twMerge(
                'size-4 transition-transform duration-200 ease-in-out',
                isOpen && 'rotate-180',
              )}
              iconColor="#344054"
            />
          </div>
        </div>
      </div>
      {/* Three layers, and each one has to be where it is. The shadow sits on the animating grid
          element because it is the only one outside the clip — `overflow-hidden` on a parent eats a
          descendant's box-shadow. The clip belongs on the middle layer, which is what makes the
          0fr→1fr row read as a reveal. The padding belongs on the innermost box so it is measured
          as part of the animated height rather than interpolated alongside it. */}
      <div
        className={twMerge(
          'grid grid-flow-row rounded-md transition-all duration-200 ease-in-out',
          isOpen ? 'grid-rows-[1fr] shadow-sm' : 'grid-rows-[0fr]',
        )}
      >
        <div className={twMerge('rounded-md', isClipped && 'overflow-hidden')}>
          <div className="px-4 py-3">{children}</div>
        </div>
      </div>
    </div>
  );
};
